import { fetchData } from '@databases/database-state';
import { BlueprintTemplate, PageTemplate, Template } from '@databases/types/confluence';

import { nextPagination } from '../pagination';

import { rejectOnError } from './utils';

/**
 * Returns the blueprints (content templates provided by Confluence or Confluence Apps) for the specified space. Global templates
 * are included, as they are inherited by the space.
 */
export function getBlueprints(
  spaceKey: string,
  expand: string[] = []
): AsyncGenerator<BlueprintTemplate, void, void> {
  return nextPagination<BlueprintTemplate>(async (url) => {
    const res = await fetchData(
      url ??
        `/rest/api/template/blueprint?${new URLSearchParams({
          spaceKey,
          expand: expand.join(','),
          limit: '200',
        })}`
    ).then(rejectOnError);
    return await res.json();
  });
}

/**
 * Returns page templates (defined by global/space admins). If the spaceKey is defined, only the page templates in the given
 * space are returned (global templates are not included). If the spaceKey is undefined, only global templates are returned.
 */
export function getPageTemplates(
  spaceKey?: string,
  expand: string[] = []
): AsyncGenerator<PageTemplate, void, void> {
  return nextPagination<PageTemplate>(async (url) => {
    const res = await fetchData(
      url ??
        `/rest/api/template/page?${new URLSearchParams({
          ...(spaceKey ? { spaceKey } : {}),
          expand: expand.join(','),
          limit: '200',
        })}`
    ).then(rejectOnError);
    return await res.json();
  });
}

/**
 * Returns all space page templates, global page templates and blueprints that are available in the given space.
 */
export async function* getAllTemplates(
  spaceKey: string,
  expand: string[] = []
): AsyncGenerator<Template, void, void> {
  const spacePageTemplates = getPageTemplates(spaceKey, expand);
  const globalPageTemplates = getPageTemplates(undefined, expand);
  // const blueprints = getBlueprints(spaceKey, expand);

  for await (const template of spacePageTemplates) {
    yield template;
  }

  for await (const template of globalPageTemplates) {
    yield template;
  }

  // For now we skip blueprints, as they are not supported well by the REST API (we cannot determine which ones
  // are actual page blueprints, and we also cannot determine their icon).

  // for await (const template of blueprints) {
  //     yield template;
  // }
}

/**
 * Retrieves a space page template, global page template, or blueprint from Confluence. The template ID is
 * enough to identify the template, regardless what type it is and what space it is part of.
 * @throws If not template with such ID exists, an error is thrown.
 */
export async function getTemplateById(
  templateId: string,
  expand: string[] = []
): Promise<Template> {
  const res = await fetchData(
    `/rest/api/template/${encodeURIComponent(templateId)}?${new URLSearchParams({
      expand: expand.join(','),
    })}`
  ).then(rejectOnError);
  return await res.json();
}
